import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getRoles } from "store/staff"
import PropTypes from "prop-types"
import { Button, Flex, Popover, Select, Switch, Tabs, Tooltip } from "antd"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import DateSwitcher from "modules/datepicker/DateSwitcher"
import styles from "../Calendar.module.css"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import { useMediaQuery } from "react-responsive"
import { DEFAULT_RANGE, RANGE_OPTIONS, DEFAULT_START_HOUR, DEFAULT_END_HOUR } from "../constants"
import { Text } from "ui/Typography"
import useActiveGuide from "hooks/useActiveClient"
import { CustomSelect } from "ui/Input/Select"
import CustomDrawer from "ui/Drawer"
import EquipmentFilters from "./EquipmentFilters"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import moment from "moment"
import { getCalendarEquipment } from "store/calendar"
import { SettingOutlined } from "@ant-design/icons"
import { DSIcon } from "ui/Icon/DSIcon"

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: moment(i, "H").format("h A"),
}))

const TimeRangeSettings = ({ startHour, endHour, onStartChange, onEndChange, onReset, isModified }) => (
  <Popover
    trigger="click"
    placement="bottomRight"
    arrow={false}
    overlayInnerStyle={{ padding: 0, borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", minWidth: 240 }}
    content={
      <div className={styles.timeRangePopover}>
        <div className={styles.timeRangePopoverHeader}>
          <span className={styles.timeRangePopoverTitle}>Visible Time Range</span>
          <div className={styles.timeRangePopoverSubtitle}>Filter bookings by time slot</div>
        </div>
        <Flex gap={12} className={styles.timeRangePopoverBody}>
          <Flex vertical gap={4} className={styles.timeRangePopoverField}>
            <span className={styles.timeRangePopoverLabel}>From</span>
            <Select
              value={startHour}
              onChange={onStartChange}
              options={HOUR_OPTIONS.filter((o) => o.value < endHour)}
              className={styles.timeRangeSelect}
            />
          </Flex>
          <Flex vertical gap={4} className={styles.timeRangePopoverField}>
            <span className={styles.timeRangePopoverLabel}>To</span>
            <Select
              value={endHour}
              onChange={onEndChange}
              options={HOUR_OPTIONS.filter((o) => o.value > startHour)}
              className={styles.timeRangeSelect}
            />
          </Flex>
        </Flex>
        <div className={styles.timeRangePopoverFooter}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            disabled={!isModified}
            onClick={onReset}
            block
          >
            Reset to Default
          </Button>
        </div>
      </div>
    }
  >
    <Button icon={<SettingOutlined className={styles.settingIcon} />} type="text" size="large" />
  </Popover>
)

TimeRangeSettings.propTypes = {
  startHour: PropTypes.number.isRequired,
  endHour: PropTypes.number.isRequired,
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  isModified: PropTypes.bool.isRequired,
}

const renderFilterBadge = (count, styles) => {
  if (!count) return null
  return <span className={styles.filterBadge}>{count}</span>
}

const removeRoleFilter = (setCrewFilters, roleId) =>
  setCrewFilters((prev) => ({ ...prev, roles: prev.roles.filter((id) => id !== roleId) }))

const removeMemberFilter = (setCrewFilters, memberId) =>
  setCrewFilters((prev) => ({ ...prev, crewMembers: prev.crewMembers.filter((id) => id !== memberId) }))

const removeEquipmentFilter = (setFilters, eqId) =>
  setFilters((prev) => ({
    ...prev,
    equipmentFilters: { ...prev.equipmentFilters, equipment: prev.equipmentFilters.equipment.filter((id) => id !== eqId) }
  }))

const removeCategoryFilter = (setFilters, catId) =>
  setFilters((prev) => ({
    ...prev,
    equipmentFilters: { ...prev.equipmentFilters, category: prev.equipmentFilters.category.filter((id) => id !== catId) }
  }))

const getFiltersFromSavedFilters = (savedFilters = {}) => ({
  experience: savedFilters?.experience_ids || [],
  crew: [
    ...(savedFilters?.unassigned ? ["unassigned"] : []),
    ...(savedFilters?.profile_ids || [])
  ],
  equipment: savedFilters?.equipment_ids || [],
  equipmentFilters: {
    equipment: [
      ...(savedFilters?.unassigned_equipment ? ["unassigned"] : []),
      ...(savedFilters?.equipment_ids || [])
    ],
    category: savedFilters?.category_ids || []
  }
})

const buildFilterPayload = ({ activeBookingType, crewFilters, filters, search }) => {
  const rawCrewIds = activeBookingType === "crew" ? crewFilters.crewMembers : filters.crew
  const isEquipment = activeBookingType === "equipment"
  return {
    profile_ids: rawCrewIds?.filter((id) => id !== "unassigned") || [],
    unassigned: rawCrewIds?.includes("unassigned") || false,
    equipment_ids: isEquipment
      ? filters.equipmentFilters?.equipment?.filter((id) => id !== "unassigned") || []
      : filters.equipment || [],
    unassigned_equipment: isEquipment
      ? filters.equipmentFilters?.equipment?.includes("unassigned") || false
      : false,
    category_ids: isEquipment ? filters.equipmentFilters?.category || [] : [],
    role_ids: activeBookingType === "crew" ? crewFilters.roles : [],
    experience_ids: filters.experience,
    search,
  }
}

const getCrewFiltersFromSavedFilters = (savedFilters = {}) => ({
  roles: savedFilters?.role_ids || [],
  crewMembers: [
    ...(savedFilters?.unassigned ? ["unassigned"] : []),
    ...(savedFilters?.profile_ids || [])
  ]
})

const renderViewSwitcherIcon = (range, agenda) => {
  const isAgendaView = agenda === true || agenda === "true"

  if (range === "date") {
    return <DSIcon name="calendarDayView" width={18} height={18} fill="currentColor" />
  }

  if (range === "month") {
    return <DSIcon name="calendarMonthView" width={18} height={18} fill="currentColor" />
  }

  if (range === "week" && isAgendaView) {
    return <DSIcon name="calendarListView" width={18} height={18} fill="currentColor" />
  }

  return <DSIcon name="calendarWeekView" width={18} height={18} fill="currentColor" />
}

const Header = ({ params, onChangeFilters, handleJournalClick, setIsScheduleTimeOffModalOpen, onFiltersChange, startHour, endHour, onStartHourChange, onEndHourChange, onResetHours, hasMyBookings, savedFilters }) => {
  const activeBookingType = params.tab || "all-bookings"
  const isAgendaView = params.range === 'week' && (params.agenda === true || params.agenda === 'true')
  const showTimeRangeSettings = params.range === 'date' || isAgendaView

  const { isMobile, isTablet } = useDevice()
  const isWideDesktop = useMediaQuery({ query: "(min-width: 1280px)" })
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { isActive: active } = useActiveGuide()
  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const date = useMemo(() => (params.date ? moment(params.date) : null), [params.date])
  const isListView = (isMobile || isTablet) && params.range === 'week' && (params.listview === true || params.listview === 'true')
  const experiences = useSelector((state) => state.experiences?.list || [])
  const crewProfiles = useSelector((state) => state.staff?.calendarCrew || [])
  const roles = useSelector((state) => state.staff?.roles?.items || [])
  const dispatch = useDispatch()
  const getCalendarFilterPopupContainer = useCallback((node) => node?.parentElement || document.body, [])
  const calendarFilterSelectProps = useMemo(() => ({
    getPopupContainer: getCalendarFilterPopupContainer
  }), [getCalendarFilterPopupContainer])

  // Fetch roles from Redux when Crew tab is active
  useEffect(() => {
    if (activeBookingType === "crew") {
      dispatch(getRoles())
    }
  }, [activeBookingType, dispatch])

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [viewPopoverOpen, setViewPopoverOpen] = useState(false)
  const [equipmentList, setEquipmentList] = useState([])

  useEffect(() => {
    dispatch(getCalendarEquipment())
      .then((res) => setEquipmentList(res.data || []))
      .catch(() => { })
  }, [dispatch])

  // Derive UI filter values directly from savedFilters (single source of truth)
  const filters = useMemo(() => getFiltersFromSavedFilters(savedFilters), [savedFilters])
  const crewFilters = useMemo(() => getCrewFiltersFromSavedFilters(savedFilters), [savedFilters])

  const setFilters = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(filters) : updater
    onFiltersChange(buildFilterPayload({ activeBookingType, crewFilters, filters: next, search: "" }))
  }, [filters, crewFilters, activeBookingType, onFiltersChange])

  const setCrewFilters = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(crewFilters) : updater
    onFiltersChange(buildFilterPayload({ activeBookingType, crewFilters: next, filters, search: "" }))
  }, [filters, crewFilters, activeBookingType, onFiltersChange])

  const experienceOptions = useMemo(() => {
    return (experiences || []).map((exp) => ({ label: exp?.title, value: exp?.id })).filter((opt) => opt.label && opt.value)
  }, [experiences])

  const crewOptions = useMemo(() => {
    return (crewProfiles || []).filter((p) => p.status?.toLowerCase() === "active").map((p) => ({ label: p.name, value: p.id }))
  }, [crewProfiles])

  const crewMemberOptionsWithUnassigned = useMemo(() => {
    const active = (crewProfiles || []).filter((p) => p.status?.toLowerCase() === "active").map((p) => ({ label: p.name, value: p.id }))
    return [{ label: "Unassigned", value: "unassigned" }, ...active]
  }, [crewProfiles])

  const equipmentOptions = useMemo(() => {
    return (equipmentList || []).map((eq) => ({ label: eq.equipment_title, value: eq.id })).filter((opt) => opt.label && opt.value)
  }, [equipmentList])

  const selectedFilters = useMemo(
    () => [
      ...filters.experience.map((value) => {
        const opt = experienceOptions.find((o) => o.value === value)
        return { type: "experience", value, label: opt?.label ?? value }
      }),
      ...filters.crew.map((value) => {
        const opt = crewOptions.find((o) => o.value === value)
        return { type: "crew", value, label: opt?.label ?? value }
      }),
      ...filters.equipment.map((value) => {
        const opt = equipmentOptions.find((o) => o.value === value)
        return { type: "equipment", value, label: opt?.label ?? value }
      })
    ],
    [filters, experienceOptions, crewOptions, equipmentOptions]
  )

  const handleFilterChange = (key) => (values) => {
    setFilters((prev) => ({ ...prev, [key]: values }))
  }

  const handleFilterRemove = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: prev[key].filter((item) => item !== value) }))
  }

  const getFilterBadge = () => {
    if (activeBookingType === "equipment") {
      return renderFilterBadge(
        (filters.equipmentFilters?.equipment?.length || 0) + (filters.equipmentFilters?.category?.length || 0),
        styles
      )
    }
    return renderFilterBadge(filters.experience.length + filters.crew.length + filters.equipment.length, styles)
  }

  const renderDesktopFilterContent = () => {
    if (activeBookingType === "crew") {
      return (
        <>
          <div className={styles.filterSelectWrapper}>
            <CustomSelect
              mode="multiple"
              placeholder="Roles"
              style={{ width: "100%" }}
              className={styles.calendarFilterSelect}
              {...calendarFilterSelectProps}
              value={crewFilters.roles}
              onChange={(values) => setCrewFilters((prev) => ({ ...prev, roles: values }))}
              options={Array.isArray(roles) ? roles.map((role) => ({ label: role.name, value: role.id })) : []}
              maxTagCount={0}
              allowClear
              showSearch={false}
              tagRender={() => null}
            />
            {crewFilters.roles.length > 0 && <div className={styles.filterCountOverlay}>{`Roles (${crewFilters.roles.length})`}</div>}
          </div>
          <div className={styles.filterSelectWrapper}>
            <CustomSelect
              mode="multiple"
              placeholder="Crew Member"
              style={{ width: "100%" }}
              className={styles.calendarFilterSelect}
              {...calendarFilterSelectProps}
              value={crewFilters.crewMembers}
              onChange={(values) => setCrewFilters((prev) => ({ ...prev, crewMembers: values }))}
              options={crewMemberOptionsWithUnassigned}
              maxTagCount={0}
              allowClear
              showSearch={false}
              tagRender={() => null}
            />
            {crewFilters.crewMembers.length > 0 && (
              <div className={styles.filterCountOverlay}>{`Crew Member (${crewFilters.crewMembers.length})`}</div>
            )}
          </div>
        </>
      )
    }
    if (activeBookingType === "equipment") {
      return (
        <EquipmentFilters
          value={filters.equipmentFilters || {}}
          onChange={(val) => setFilters((prev) => ({ ...prev, equipmentFilters: val }))}
          onOptionsChange={({ equipmentOptions, categoryOptions }) => {
            setFilters((prev) => ({ ...prev, equipmentOptions, categoryOptions }))
          }}
          hideSearch
          selectProps={calendarFilterSelectProps}
        />
      )
    }
    return (
      <>
        <div className={styles.filterSelectWrapper}>
          <CustomSelect
            mode="multiple"
            placeholder={t("calendar.filters.experience", "Experience")}
            className={styles.calendarFilterSelect}
            style={{ width: "100%" }}
            {...calendarFilterSelectProps}
            value={filters.experience}
            onChange={handleFilterChange("experience")}
            options={experienceOptions}
            maxTagCount={0}
            allowClear
            showSearch={false}
            tagRender={() => null}
          />
          {filters.experience.length > 0 && (
            <div className={styles.filterCountOverlay}>
              {`${t("calendar.filters.experience", "Experience")} (${filters.experience.length})`}
            </div>
          )}
        </div>
        {activeBookingType !== "my-bookings" && (
          <div className={styles.filterSelectWrapper}>
            <CustomSelect
              mode="multiple"
              style={{ width: "100%" }}
              placeholder={t("calendar.filters.crew_member", "Crew Member")}
              className={styles.calendarFilterSelect}
              {...calendarFilterSelectProps}
              value={filters.crew}
              onChange={handleFilterChange("crew")}
              options={crewMemberOptionsWithUnassigned}
              maxTagCount={0}
              allowClear
              showSearch={false}
              tagRender={() => null}
            />
            {filters.crew.length > 0 && (
              <div className={styles.filterCountOverlay}>
                {`${t("calendar.filters.crew_member", "Crew Member")} (${filters.crew.length})`}
              </div>
            )}
          </div>
        )}
        <div className={styles.filterSelectWrapper}>
          <CustomSelect
            mode="multiple"
            placeholder={t("calendar.filters.equipment", "Equipment")}
            className={styles.calendarFilterSelect}
            style={{ width: "100%" }}
            {...calendarFilterSelectProps}
            value={filters.equipment}
            onChange={handleFilterChange("equipment")}
            options={equipmentOptions}
            maxTagCount={0}
            allowClear
            showSearch={false}
            tagRender={() => null}
          />
          {filters.equipment.length > 0 && (
            <div className={styles.filterCountOverlay}>
              {`${t("calendar.filters.equipment", "Equipment")} (${filters.equipment.length})`}
            </div>
          )}
        </div>
      </>
    )
  }

  const renderMobileFilterContent = () => {
    if (activeBookingType === "crew") {
      return (
        <>
          <CustomSelect
            mode="multiple"
            style={{ width: "100%" }}
            placeholder={crewFilters.roles.length > 0 ? `Roles (${crewFilters.roles.length})` : "Roles"}
            className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`}
            {...calendarFilterSelectProps}
            value={crewFilters.roles}
            onChange={(values) => setCrewFilters((prev) => ({ ...prev, roles: values }))}
            options={Array.isArray(roles) ? roles.map((role) => ({ label: role.name, value: role.id })) : []}
            maxTagCount="responsive"
            allowClear
            tagRender={renderFilterTag}
          />
          <CustomSelect
            mode="multiple"
            placeholder={crewFilters.crewMembers.length > 0 ? `Crew Member (${crewFilters.crewMembers.length})` : "Crew Member"}
            className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`}
            style={{ width: "100%" }}
            {...calendarFilterSelectProps}
            value={crewFilters.crewMembers}
            onChange={(values) => setCrewFilters((prev) => ({ ...prev, crewMembers: values }))}
            options={crewMemberOptionsWithUnassigned}
            maxTagCount="responsive"
            allowClear
            tagRender={renderFilterTag}
          />
        </>
      )
    }
    if (activeBookingType === "equipment") {
      return (
        <EquipmentFilters
          value={filters.equipmentFilters || {}}
          onChange={(val) => setFilters((prev) => ({ ...prev, equipmentFilters: val }))}
          onOptionsChange={({ equipmentOptions, categoryOptions }) => {
            setFilters((prev) => ({ ...prev, equipmentOptions, categoryOptions }))
          }}
          hideSearch
          fullWidth
          selectProps={calendarFilterSelectProps}
        />
      )
    }
    return (
      <>
        {activeBookingType !== "my-bookings" && (
          <CustomSelect
            mode="multiple"
            placeholder={t("calendar.filters.crew_member", "Crew Member")}
            className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`}
            style={{ width: "100%" }}
            {...calendarFilterSelectProps}
            value={filters.crew}
            onChange={handleFilterChange("crew")}
            options={crewMemberOptionsWithUnassigned}
            maxTagCount="responsive"
            allowClear
            tagRender={renderFilterTag}
          />
        )}
        <CustomSelect
          mode="multiple"
          placeholder={t("calendar.filters.equipment", "Equipment")}
          className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`}
          style={{ width: "100%" }}
          {...calendarFilterSelectProps}
          value={filters.equipment}
          onChange={handleFilterChange("equipment")}
          options={equipmentOptions}
          maxTagCount="responsive"
          allowClear
          tagRender={renderFilterTag}
        />
        {params.range === "date" && (
          <CustomSelect
            mode="multiple"
            placeholder={t("calendar.filters.experience", "Experience")}
            className={`${styles.calendarFilterSelect} ${styles.filterSearchFull}`}
            style={{ width: "100%" }}
            {...calendarFilterSelectProps}
            value={filters.experience}
            onChange={handleFilterChange("experience")}
            options={experienceOptions}
            maxTagCount="responsive"
            allowClear
            tagRender={renderFilterTag}
          />
        )}
      </>
    )
  }

  const renderFilterTag = ({ label, closable, onClose }) => (
    <span className={styles.filterTag}>
      <span className={styles.filterTagLabel}>{label}</span>
      {closable && (
        <button type="button" className={styles.filterTagRemove} onClick={onClose} aria-label="Remove">
          <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
            <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </span>
  )

  useEffect(() => {
    if (!params.range) onChangeFilters({ target: { value: DEFAULT_RANGE, name: "range" } })
  }, [params.range]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!params.range) return null

  const handleClick = () => {
    navigate(routes.guideBookingNewPath())
  }

  return (
    <Flex vertical className={styles.calendarHeader}>
      {/* 1. Booking type buttons */}
      <Flex gap={8} className={`px-s py-xs ${styles.bookingTypeButtons}`}>
        <Button
          size="middle"
          type={activeBookingType === "all-bookings" ? "primary" : "default"}
          onClick={() => onChangeFilters({ target: { name: "tab", value: "all-bookings" } })}
        >
          All Bookings
        </Button>
        <Tooltip title={hasMyBookings === false ? "No bookings assigned to you" : null}>
          <span>
            <Button
              size="middle"
              type={activeBookingType === "my-bookings" ? "primary" : "default"}
              onClick={() => onChangeFilters({ target: { name: "tab", value: "my-bookings" } })}
              className={hasMyBookings ? "" : styles.disabledButton}
              disabled={!hasMyBookings}
            >
              My Bookings
            </Button>
          </span>
        </Tooltip>
        <Button
          size="middle"
          type={activeBookingType === "crew" ? "primary" : "default"}
          onClick={() => onChangeFilters({ target: { name: "tab", value: "crew" } })}
        >
          Crew
        </Button>
        <Button
          size="middle"
          type={activeBookingType === "equipment" ? "primary" : "default"}
          onClick={() => onChangeFilters({ target: { name: "tab", value: "equipment" } })}
        >
          Equipment
        </Button>
      </Flex>

      {/* 2. DateSwitcher + Tabs (Day/Week/Month) row — ABOVE filters */}
      <Flex gap={8} align="center" justify="flex-start" className={`px-m py-xs ${styles.calendarActionsWrapper}`} wrap>
        {/* Desktop: DateSwitcher + Tabs + Legend */}
        {!(isMobile || isTablet) && (
          <Flex align="center" gap={8} justify="space-between" className="w-full" wrap>
            <Flex align="center" gap={16} className={styles.dateSwitcherFlex}>
              <DateSwitcher
                className={`${styles.calendarSwitcher}`}
                value={date}
                defaultValue={moment().startOf("day")}
                name="date"
                onChange={onChangeFilters}
                type={params.range}
                isCompleteDateRange={true}
              />
              <div className={styles.tabsContainer}>
                <Tabs
                  activeKey={params.range}
                  onChange={(key) => {
                    onChangeFilters({ target: { name: "range", value: key } })
                  }}
                  className={styles.antDTabs}
                >
                  {Object.entries(RANGE_OPTIONS).map(([range, label]) => (
                    <Tabs.TabPane tab={label} key={range} />
                  ))}
                </Tabs>
              </div>
              {params.range === "week" && activeBookingType === "all-bookings" && (
                <Flex align="center" gap={8} className={styles.agendaToggle}>
                  <Switch
                    checked={params.agenda === true || params.agenda === "true"}
                    onChange={(checked) => onChangeFilters({ target: { name: "agenda", value: checked } })}
                    size="small"
                  />
                  <Text className={styles.agendaToggleText}>{t("calendar.agenda_view", "Agenda View")}</Text>
                </Flex>
              )}
            </Flex>
            <Flex align="center" gap={16} className={styles.desktopLegendGroup}>
              <Button
                color="primary"
                type="default"
                variant="outlined"
                className={styles.goToToday}
                size="middle"
                disabled={!date || date.isSame(moment(), "day")}
                onClick={() => {
                  onChangeFilters({ target: { name: "date", value: moment().format("YYYY-MM-DD") } })
                }}
              >
                {t("calendar.go_to_today", "Go to Today")}
              </Button>
              <Flex gap={8} align="center">
                <Text className={styles.headerLegendLabel}>{t("calendar.legend")}:</Text>
                <Flex gap={8}>
                  <Flex gap={4} className={styles.headerLegendItem}>
                    <span className={styles.headerLegendPaid} />
                    <Text className={styles.headerLegendText}>{t("calendar.legend_paid")}</Text>
                  </Flex>
                  <Flex gap={4} className={styles.headerLegendItem}>
                    <span className={styles.headerLegendOutstanding} />
                    <Text className={styles.headerLegendText}>{t("calendar.legend_outstanding")}</Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        )}

        {/* Tablet (768–1024px): Day/Week/Month tabs + DateSwitcher + icons */}
        {isTablet && (
          <Flex align="center" justify="space-between" gap={8} className={`${styles.crewInlineRow} w-full`}>
            {/* LEFT: Day/Week/Month tab buttons */}
            <Flex align="center" gap={0} className={styles.agendaToggle}>
              {[
                { range: "date", label: "Day" },
                { range: "week", label: "Week" },
                { range: "month", label: "Month" }
              ].map(({ range: r, label }) => (
                <button
                  key={r}
                  type="button"
                  className={`${styles.mobileViewTab} ${params.range === r ? styles.mobileViewTabActive : ""}`}
                  onClick={() => {
                    onChangeFilters({ target: { name: "range", value: r } })
                    onChangeFilters({ target: { name: "listview", value: false } })
                  }}
                >
                  {label}
                </button>
              ))}
            </Flex>
            {/* MIDDLE: DateSwitcher */}
            <Flex className={`${styles.dateSwitcherMiddle} flex-row-reverse`}>
              <DateSwitcher
                className={`${styles.calendarSwitcher}`}
                value={date}
                defaultValue={moment().startOf("day")}
                name="date"
                onChange={onChangeFilters}
                type={isListView ? 'date' : params.range}
                isCompleteDateRange={true}
              />
              {/* RIGHT: filter (non-crew) + export icons */}
              <Flex align="center" gap={8}>
                {activeBookingType !== "crew" && (
                  <button
                    type="button"
                    className={`${styles.mobileHeaderIcon} ${styles.iconBtnReset} ${styles.mobileFilters}`}
                    onClick={() => setMobileFiltersOpen(true)}
                    aria-label="Filters"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    {getFilterBadge()}
                  </button>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}

        {/* Mobile (≤767px): DateSwitcher + view-switcher icon (original design) */}
        {isMobile && (
          <Flex align="center" justify="space-between" wrap gap={12} className={`${styles.crewInlineRow} w-full`}>
            <div className={styles.dateSwitcherFlex}>
              <DateSwitcher
                className={`${styles.calendarSwitcher}`}
                value={date}
                defaultValue={moment().startOf("day")}
                name="date"
                onChange={onChangeFilters}
                type={isListView ? 'date' : params.range}
                isCompleteDateRange={true}
              />
            </div>
            <Flex align="center" gap={8} className="ml-auto">
              {activeBookingType !== "crew" && (
                <button
                  type="button"
                  className={`${styles.mobileHeaderIcon} ${styles.iconBtnReset}`}
                  onClick={() => setMobileFiltersOpen(true)}
                  aria-label="Filters"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                  </svg>
                  {getFilterBadge()}
                </button>
              )}
              <Popover
                open={viewPopoverOpen}
                onOpenChange={setViewPopoverOpen}
                trigger="click"
                placement="bottomRight"
                arrow={false}
                overlayInnerStyle={{ padding: 0, borderRadius: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.13)" }}
                content={
                  <div className={styles.viewSwitcherPopup}>
                    {[
                      { label: "Day View", key: "day", range: "date", listview: false },
                      { label: "Week View", key: "week", range: "week", listview: false },
                      { label: "Month View", key: "month", range: "month", listview: false },
                      { label: "List View", key: "list", range: "week", listview: true }
                    ].map(({ label, key, range, listview: isListViewOption }) => {
                      const isExplicitList = params.listview === true || params.listview === "true"
                      const isActive = isListViewOption
                        ? params.range === range && isExplicitList
                        : params.range === range && !isExplicitList
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`${styles.viewSwitcherOption} ${isActive ? styles.viewSwitcherOptionActive : ""}`}
                          onClick={() => {
                            onChangeFilters({ target: { name: "range", value: range } })
                            onChangeFilters({ target: { name: "listview", value: isListViewOption } })
                            if (isListViewOption) {
                              onChangeFilters({ target: { name: 'date', value: moment().format('YYYY-MM-DD') } })
                            }
                            setViewPopoverOpen(false)
                          }}
                        >
                          <span className={styles.viewSwitcherOptionLabel}>{label}</span>
                        </button>
                      )
                    })}
                  </div>
                }
              >
                <button type="button" className={`${styles.mobileHeaderIcon} ${styles.iconBtnPrimary}`} aria-label="Switch view">
                  {renderViewSwitcherIcon(params.range, params.agenda)}
                </button>
              </Popover>
            </Flex>
          </Flex>
        )}

        {/* Inline crew filters for mobile — Roles + Crew Member shown directly */}
        {(isMobile || isTablet) && activeBookingType === "crew" && (
          <Flex gap={8} className={`${styles.crewInlineRow} px-m py-xs`}>
            <div className={styles.filterSelectFlex}>
              <CustomSelect
                mode="multiple"
                placeholder="Roles"
                className={styles.filterSearchFull}
                {...calendarFilterSelectProps}
                value={crewFilters.roles}
                onChange={(values) => setCrewFilters((prev) => ({ ...prev, roles: values }))}
                options={Array.isArray(roles) ? roles.map((role) => ({ label: role.name, value: role.id })) : []}
                maxTagCount={0}
                allowClear
                showSearch={false}
                tagRender={() => null}
              />
              {crewFilters.roles.length > 0 && <div className={styles.filterCountOverlay}>{`Roles (${crewFilters.roles.length})`}</div>}
            </div>
            <div className={styles.filterSelectFlex}>
              <CustomSelect
                mode="multiple"
                placeholder="Crew Member"
                className={styles.filterSearchFull}
                {...calendarFilterSelectProps}
                value={crewFilters.crewMembers}
                onChange={(values) => setCrewFilters((prev) => ({ ...prev, crewMembers: values }))}
                options={crewMemberOptionsWithUnassigned}
                maxTagCount={0}
                allowClear
                showSearch={false}
                tagRender={() => null}
              />
              {crewFilters.crewMembers.length > 0 && (
                <div className={styles.filterCountOverlay}>{`Crew Member (${crewFilters.crewMembers.length})`}</div>
              )}
            </div>
          </Flex>
        )}
      </Flex>

      {/* 3. Desktop: inline filters — BELOW the tabs row */}
      {!(isMobile) && (
        <Flex justify="space-between" className={`px-s py-xs ${styles.calendarFiltersRow}`} gap={8} wrap>
          <Flex gap={12} align="center" className={styles.calendarFilters}>
            {renderDesktopFilterContent()}
          </Flex>
          {isWideDesktop && (
            <Flex gap={10} className={`${styles.calendarActions} ml-auto`}>
              {canCreateJournal && (isMobile || isTablet) && (
                <Button
                  color="primary"
                  variant="outlined"
                  className={`${styles.addJournal} px-m py-s`}
                  size="large"
                  onClick={active ? handleJournalClick : undefined}
                  disabled={!active}
                  aria-disabled={!active}
                >
                  {t("calendar.journal.add_journal")}
                </Button>
              )}
              {(activeBookingType === "my-bookings" || activeBookingType === "crew") && (
                <Button
                  color="primary"
                  variant="outlined"
                  className={`${styles.scheduleTimeOff}`}
                  size="middle"
                  onClick={active ? () => setIsScheduleTimeOffModalOpen(true) : undefined}
                  disabled={!active}
                  aria-disabled={!active}
                >
                  {t("unavailable_days.schedule_time_off")}
                </Button>
              )}
              {canCreateBooking && (
                <Button
                  type="primary"
                  className={`${styles.newBooking}`}
                  size="middle"
                  onClick={active ? handleClick : undefined}
                  disabled={!active}
                  aria-disabled={!active}
                >
                  {t("bookings.add")}
                </Button>
              )}
              {showTimeRangeSettings && (
                <TimeRangeSettings
                  startHour={startHour}
                  endHour={endHour}
                  onStartChange={onStartHourChange}
                  onEndChange={onEndHourChange}
                  onReset={onResetHours}
                  isModified={startHour !== DEFAULT_START_HOUR || endHour !== DEFAULT_END_HOUR}
                />
              )}
            </Flex>
          )}
        </Flex>
      )}

      {/* 4. Mobile: filter bottom sheet */}
      {(isMobile || isTablet) && (
        <CustomDrawer open={mobileFiltersOpen} setOpen={setMobileFiltersOpen} title="Filters">
          <Flex vertical gap={10}>
            {renderMobileFilterContent()}
            <Flex gap={12}>
              <Button size="large" className="flex-1" type="primary" onClick={() => setMobileFiltersOpen(false)}>
                Apply
              </Button>
              <Button
                size="large" className="flex-1"
                color="primary"
                variant="outlined"
                // className={styles.clearFiltersLink}
                onClick={() => {
                  if (activeBookingType === "crew") {
                    setCrewFilters({ roles: [], crewMembers: [] })
                  } else if (activeBookingType === "equipment") {
                    setFilters((prev) => ({ ...prev, equipmentFilters: { equipment: [], category: [] } }))
                  } else {
                    setFilters({ experience: [], crew: [], equipment: [] })
                  }
                  setMobileFiltersOpen(false)
                }}
              >
                Clear Filters
              </Button>
            </Flex>
          </Flex>
        </CustomDrawer>
      )}

      {/* 5. Selected filter chips */}
      {activeBookingType === "crew" && (crewFilters.roles.length > 0 || crewFilters.crewMembers.length > 0) && (
        <Flex gap={8} className={styles.selectedFilters} wrap="wrap">
          {crewFilters.roles.map((roleId) => {
            const roleObj = (Array.isArray(roles) ? roles : []).find((r) => r.id === roleId)
            return (
              <span key={`role-${roleId}`} className={styles.filterChip}>
                <span className={styles.filterChipLabel}>{roleObj ? roleObj.name : roleId}</span>
                <button
                  type="button"
                  className={styles.filterChipRemove}
                  onClick={() => removeRoleFilter(setCrewFilters, roleId)}
                  aria-label="Remove"
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                    <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            )
          })}
          {crewFilters.crewMembers.map((memberId) => {
            const memberObj = (Array.isArray(crewProfiles) ? crewProfiles : []).find((m) => m.id === memberId)
            return (
              <span key={`crew-${memberId}`} className={styles.filterChip}>
                <span className={styles.filterChipLabel}>{memberObj ? memberObj.name : memberId}</span>
                <button
                  type="button"
                  className={styles.filterChipRemove}
                  onClick={() => removeMemberFilter(setCrewFilters, memberId)}
                  aria-label="Remove"
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                    <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            )
          })}
        </Flex>
      )}
      {activeBookingType === "equipment" &&
        filters.equipmentFilters &&
        (filters.equipmentFilters.equipment || filters.equipmentFilters.category) && (
          <Flex gap={8} className={styles.selectedFilters} wrap="wrap">
            {Array.isArray(filters.equipmentFilters.equipment) &&
              filters.equipmentFilters.equipment.map((eqId) => {
                const eqObj = (filters.equipmentOptions || []).find((eq) => eq.value === eqId)
                return (
                  <span key={`equipment-${eqId}`} className={styles.filterChip}>
                    <span className={styles.filterChipLabel}>{eqObj ? eqObj.label : eqId}</span>
                    <button
                      type="button"
                      className={styles.filterChipRemove}
                      onClick={() => removeEquipmentFilter(setFilters, eqId)}
                      aria-label="Remove"
                    >
                      <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                        <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </span>
                )
              })}
            {Array.isArray(filters.equipmentFilters.category) &&
              filters.equipmentFilters.category.map((catId) => {
                const catObj = (filters.categoryOptions || []).find((cat) => cat.value === catId)
                return (
                  <span key={`category-${catId}`} className={styles.filterChip}>
                    <span className={styles.filterChipLabel}>{catObj ? catObj.label : catId}</span>
                    <button
                      type="button"
                      className={styles.filterChipRemove}
                      onClick={() => removeCategoryFilter(setFilters, catId)}
                      aria-label="Remove"
                    >
                      <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                        <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </span>
                )
              })}
          </Flex>
        )}
      {activeBookingType !== "crew" && activeBookingType !== "equipment" && selectedFilters.length > 0 && (
        <Flex gap={8} className={styles.selectedFilters} wrap="wrap">
          {selectedFilters.map((filter) => (
            <span key={`${filter.type}-${filter.value}`} className={styles.filterChip}>
              <span className={styles.filterChipLabel}>{filter.label}</span>
              <button
                type="button"
                className={styles.filterChipRemove}
                onClick={() => handleFilterRemove(filter.type, filter.value)}
                aria-label="Remove"
              >
                <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                  <path d="M3 3l6 6M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}
        </Flex>
      )}

      {/* 6. Mobile legend row */}
      {(isMobile || isTablet) && (
        <Flex align="center" wrap gap={8} justify="space-between" className="w-full px-s">
          <Flex gap={12} align="center" justify="space-between" className={`py-xs ${styles.mobileLegendRow}`}>
            <Flex gap={12} align="center">
              <Text className={styles.headerLegendLabel}>{t("calendar.legend")}:</Text>
              <Flex gap={16}>
                <Flex gap={4} className={styles.headerLegendItem}>
                  <span className={styles.headerLegendPaid} />
                  <Text className={styles.headerLegendText}>{t("calendar.legend_paid")}</Text>
                </Flex>
                <Flex gap={4} className={styles.headerLegendItem}>
                  <span className={styles.headerLegendOutstanding} />
                  <Text className={styles.headerLegendText}>{t("calendar.legend_outstanding")}</Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          {params.range === "week" && activeBookingType === "all-bookings" && !isListView && (
            <Flex align="center" gap={8} className={styles.agendaToggle}>
              <Switch
                checked={params.agenda === true || params.agenda === "true"}
                onChange={(checked) => onChangeFilters({ target: { name: "agenda", value: checked } })}
                size="small"
              />
              <Text className={styles.agendaToggleText}>{t("calendar.agenda_view", "Agenda View")}</Text>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  )
}

Header.propTypes = {
  params: PropTypes.shape({
    date: PropTypes.string,
    range: PropTypes.string,
    weather: PropTypes.bool,
    tab: PropTypes.string,
    agenda: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    listview: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  handleJournalClick: PropTypes.func.isRequired,
  setIsScheduleTimeOffModalOpen: PropTypes.func.isRequired,
  onFiltersChange: PropTypes.func,
  startHour: PropTypes.number,
  endHour: PropTypes.number,
  onStartHourChange: PropTypes.func,
  onEndHourChange: PropTypes.func,
  onResetHours: PropTypes.func,
  hasMyBookings: PropTypes.bool,
  savedFilters: PropTypes.object,
}

export default Header
